import React, { useEffect, useRef, useState } from 'react';
import { DollarSign, Zap, MapPin, X, Loader2, ExternalLink, ArrowRight, Check, UserPlus } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import http, { blackout } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';

const GOLDEN_MAP_SRC =
  'https://customer-assets-eiarnc6j.emergentagent.net/job_promote-commerce/artifacts/3q08aiqa_blackout.png';

// --- Lat/Lng → (x%, y%) projection for the golden-US artwork --------------
// The artwork is roughly an Albers-USA-style silhouette. These bounds were
// hand-calibrated against the image's state outlines so a real lat/lng
// (e.g. 40.7128,-74.0060 = NYC) lands dead-center on New York.
const PROJ = {
  // (west, east, north, south) of the artwork's continental-US silhouette
  west: -124.5, east: -66.5,
  north: 49.6,  south: 24.5,
  // artwork padding — the drawing doesn't fill the whole image
  xMin: 10.5, xMax: 92.0,
  yMin: 7.5,  yMax: 86.0,
};
function projectLatLng(lat, lng) {
  if (lat == null || lng == null) return null;
  const xPct = PROJ.xMin + ((lng - PROJ.west) / (PROJ.east - PROJ.west)) * (PROJ.xMax - PROJ.xMin);
  const yPct = PROJ.yMin + ((PROJ.north - lat) / (PROJ.north - PROJ.south)) * (PROJ.yMax - PROJ.yMin);
  return { x: xPct, y: yPct };
}

/** The Golden Map on the public homepage. The user-provided golden-US artwork
 *  serves as the map background, with Black $ markers for active paid listings
 *  and animated Black Dots for Blackout Game discoveries placed on top at
 *  real geocoded coordinates (falls back to state-centroid approximation). */
export default function GoldenMap() {
  const [data, setData] = useState({ paid: [], discovered: [] });
  const [err, setErr] = useState(false);
  const [tip, setTip] = useState(null);
  const [open, setOpen] = useState(null); // { kind, id }
  const timerRef = useRef(null);

  const load = async () => {
    try {
      const res = await http.get('/map/markers');
      setData(res.data || { paid: [], discovered: [] });
      setErr(false);
    } catch {
      setErr(true);
    }
  };

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, 20_000);
    return () => clearInterval(timerRef.current);
  }, []);

  // Enrich each marker with its (x%, y%) on the artwork using real lat/lng.
  const place = (m) => {
    const p = projectLatLng(m.lat, m.lng);
    return p ? { ...m, x: p.x, y: p.y } : null;
  };
  const paidPts = data.paid.map(place).filter(Boolean);
  const discPts = data.discovered.map(place).filter(Boolean);

  // Cluster markers within 1.1% × 1.1% of each other so a dense city doesn't
  // stack unreadable overlays. A paid-only cluster shows $, a mixed cluster
  // shows the count in a bordered pill.
  const clusters = clusterMarkers([...paidPts, ...discPts], 40);
  const [openCluster, setOpenCluster] = useState(null);

  return (
    <section className="relative" data-testid="golden-map-section">
      <style>{`
        .pbb-gmap-wrap { position: relative; width: 100%; aspect-ratio: 1536 / 960; max-width: 1200px; margin: 0 auto; }
        .pbb-gmap-img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: contain; user-select: none; pointer-events: none; }
        .pbb-gmap-marker {
          position: absolute; transform: translate(-50%, -50%);
          display: flex; align-items: center; justify-content: center;
          border-radius: 999px; cursor: pointer; z-index: 2;
          transition: transform .2s ease, box-shadow .2s ease;
        }
        .pbb-gmap-marker:hover { transform: translate(-50%, -50%) scale(1.25); z-index: 10; }
        .pbb-gmap-paid {
          width: clamp(22px, 2.1vw, 32px); height: clamp(22px, 2.1vw, 32px);
          background: linear-gradient(135deg, #000 0%, #111 100%);
          color: #ffd54a;
          border: 2px solid #ffd54a;
          box-shadow: 0 0 0 3px rgba(0,0,0,.7), 0 0 14px rgba(255,213,74,.9), 0 0 28px rgba(255,170,0,.5);
          animation: pbbPaidPop .9s ease;
        }
        .pbb-gmap-dot {
          width: clamp(14px, 1.3vw, 18px); height: clamp(14px, 1.3vw, 18px);
          background: #000; border: 2px solid #fff;
          box-shadow: 0 0 0 2px rgba(0,0,0,.75), 0 2px 6px rgba(0,0,0,.6);
        }
        .pbb-gmap-dot::before {
          content: ""; position: absolute; inset: -5px; border-radius: 999px;
          box-shadow: 0 0 0 0 rgba(255,255,255,.7);
          animation: pbbDotPulse 1.6s ease-out infinite;
        }
        @keyframes pbbDotPulse { 0% { box-shadow:0 0 0 0 rgba(255,255,255,.7);} 70% { box-shadow:0 0 0 14px rgba(255,255,255,0);} 100% { box-shadow:0 0 0 0 rgba(255,255,255,0);} }
        @keyframes pbbPaidPop { 0% { transform: translate(-50%,-50%) scale(.3); opacity: 0; } 60% { transform: translate(-50%,-50%) scale(1.25);} 100% { transform: translate(-50%,-50%) scale(1); opacity: 1; } }
        .pbb-gmap-cluster {
          position: absolute; transform: translate(-50%, -50%);
          display: flex; align-items: center; justify-content: center;
          border-radius: 999px; cursor: pointer; font-weight: 900;
          font-size: clamp(11px, 0.95vw, 14px); line-height: 1;
          color: #000;
          width: clamp(30px, 2.5vw, 40px); height: clamp(30px, 2.5vw, 40px);
          transition: transform .2s ease;
        }
        .pbb-gmap-cluster:hover { transform: translate(-50%, -50%) scale(1.15); z-index: 10; }
        .pbb-gmap-cluster-paid {
          background: linear-gradient(135deg, #ffd54a 0%, #f59e0b 70%, #b45309 100%);
          border: 2px solid #000;
          box-shadow: 0 0 0 3px rgba(0,0,0,.7), 0 0 18px rgba(255,170,0,.7);
        }
        .pbb-gmap-cluster-mixed {
          background: #000; color: #fff;
          border: 2px solid #ffd54a;
          box-shadow: 0 0 0 2px rgba(0,0,0,.7), 0 0 14px rgba(255,213,74,.6);
        }
        .pbb-gmap-tip {
          position: absolute; transform: translate(-50%, calc(-100% - 16px));
          background: #0a0a0a; color: #f5f5f5; border: 1px solid rgba(255,213,74,.4);
          border-radius: 10px; padding: 8px 10px; min-width: 180px; max-width: 240px;
          box-shadow: 0 10px 30px rgba(0,0,0,.6); pointer-events: none; z-index: 20;
          font-family: inherit; font-size: 12px; line-height: 1.4;
        }
        .pbb-gmap-tip::after {
          content: ""; position: absolute; left: 50%; bottom: -6px; transform: translateX(-50%) rotate(45deg);
          width: 10px; height: 10px; background: #0a0a0a; border-right: 1px solid rgba(255,213,74,.4); border-bottom: 1px solid rgba(255,213,74,.4);
        }
      `}</style>

      <div className="flex items-end justify-between mb-3">
        <div>
          <h2 className="text-2xl md:text-3xl font-black" data-testid="golden-map-title">The Golden Map</h2>
          <p className="text-sm text-neutral-400">
            Live view of our Black-owned business network.
            <span className="text-amber-300 font-semibold"> Black $</span> = active paid listings,
            <span className="text-white"> ●</span> = Blackout Game discoveries awaiting upgrade.
          </p>
        </div>
        <div className="hidden md:flex items-center gap-4 text-xs">
          <LegendDot label={`${paidPts.length} active`} tint="amber" testId="legend-paid"/>
          <LegendDot label={`${discPts.length} discovered`} tint="white" testId="legend-discovered"/>
        </div>
      </div>

      <div className="rounded-2xl overflow-hidden border border-neutral-800 bg-black" data-testid="golden-map-canvas">
        <div className="pbb-gmap-wrap">
          <img src={GOLDEN_MAP_SRC} alt="Golden map of the United States" className="pbb-gmap-img" draggable={false}/>

          {err && (
            <div className="absolute top-3 left-3 bg-black/70 text-amber-300 text-[11px] px-2 py-1 rounded border border-amber-500/40 inline-flex items-center gap-1 z-20">
              <MapPin size={11}/> Map data loading…
            </div>
          )}

          {clusters.map(c => {
            if (c.items.length === 1) {
              const m = c.items[0];
              const isPaid = m.kind === 'paid';
              return (
                <button
                  key={`m-${m.kind}-${m.id}`}
                  className={isPaid ? 'pbb-gmap-marker pbb-gmap-paid' : 'pbb-gmap-marker pbb-gmap-dot'}
                  style={{ left: `${m.x}%`, top: `${m.y}%` }}
                  onMouseEnter={() => setTip({ ...m, paid: isPaid })}
                  onMouseLeave={() => setTip(null)}
                  onFocus={() => setTip({ ...m, paid: isPaid })}
                  onBlur={() => setTip(null)}
                  onClick={() => setOpen({ kind: isPaid ? 'paid' : 'discovered', id: m.id, seed: m })}
                  data-testid={isPaid ? `map-paid-${m.id}` : `map-dot-${m.id}`}
                  aria-label={`${m.name} — ${isPaid ? 'active paid listing' : 'discovery pending upgrade'}`}
                >
                  {isPaid && <DollarSign size={14} strokeWidth={3}/>}
                </button>
              );
            }
            // Cluster — N markers within the same ~1% square
            const paidCount = c.items.filter(i => i.kind === 'paid').length;
            const allPaid = paidCount === c.items.length;
            return (
              <button
                key={`c-${c.items[0].id}`}
                className={allPaid ? 'pbb-gmap-cluster pbb-gmap-cluster-paid' : 'pbb-gmap-cluster pbb-gmap-cluster-mixed'}
                style={{ left: `${c.x}%`, top: `${c.y}%` }}
                onClick={() => setOpenCluster(c)}
                data-testid={`map-cluster-${c.items[0].id}`}
                aria-label={`${c.items.length} listings in this area (${paidCount} paid)`}
                title={`${c.items.length} listings · ${paidCount} paid`}
              >
                {c.items.length}
              </button>
            );
          })}

          {tip && <MarkerTip tip={tip}/>}
        </div>
      </div>

      {openCluster && (
        <ClusterListModal
          cluster={openCluster}
          onClose={() => setOpenCluster(null)}
          onPick={(item) => {
            setOpenCluster(null);
            setOpen({ kind: item.kind === 'paid' ? 'paid' : 'discovered', id: item.id, seed: item });
          }}
        />
      )}

      {open && (
        <MarkerDetailModal
          kind={open.kind}
          id={open.id}
          seed={open.seed}
          onClose={() => setOpen(null)}
        />
      )}
    </section>
  );
}

// ------------- clustering utility -----------------
const miles = (a, b) => {
  const R = 3959, r = Math.PI / 180;
  const dLat = (b.lat - a.lat) * r, dLng = (b.lng - a.lng) * r;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * r) * Math.cos(b.lat * r) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

// Group listings into metro-area clusters: every listing within `radiusMi` of the
// cluster's first (seed) listing joins it, so a whole city counts as one bubble.
function clusterMarkers(points, radiusMi) {
  const clusters = [];
  for (const p of points) {
    const c = clusters.find(c => miles(c.seed, p) <= radiusMi);
    if (c) {
      c.items.push(p);
      c.x = (c.x * (c.items.length - 1) + p.x) / c.items.length;
      c.y = (c.y * (c.items.length - 1) + p.y) / c.items.length;
    } else {
      clusters.push({ seed: p, x: p.x, y: p.y, items: [p] });
    }
  }
  return clusters;
}

function ClusterListModal({ cluster, onClose, onPick }) {
  return (
    <div className="fixed inset-0 z-[95] bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="map-cluster-modal">
      <div className="w-full max-w-sm bg-[#0a0a0a] border border-amber-500/40 rounded-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-neutral-800 flex items-center justify-between">
          <div className="font-bold text-sm">{cluster.items.length} listings in this area</div>
          <button onClick={onClose} className="text-neutral-400 hover:text-white"><X size={16}/></button>
        </div>
        <div className="divide-y divide-neutral-800 max-h-80 overflow-y-auto">
          {cluster.items.map(item => (
            <button
              key={`${item.kind}-${item.id}`}
              onClick={() => onPick(item)}
              className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-neutral-900 transition-colors"
              data-testid={`cluster-item-${item.id}`}
            >
              {item.kind === 'paid'
                ? <span className="w-7 h-7 rounded-full bg-amber-500 text-black inline-flex items-center justify-center shrink-0"><DollarSign size={13} strokeWidth={3}/></span>
                : <span className="w-7 h-7 rounded-full bg-black border-2 border-white inline-flex items-center justify-center shrink-0"><Zap size={11} className="text-white"/></span>}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate text-white">{item.name}</div>
                <div className="text-[11px] text-neutral-400">{[item.city, item.state].filter(Boolean).join(', ')}</div>
              </div>
              <ArrowRight size={14} className="text-neutral-500"/>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function LegendDot({ label, tint, testId }) {
  return (
    <div className="flex items-center gap-1.5 text-neutral-300" data-testid={testId}>
      <span className={`inline-block w-3 h-3 rounded-full border ${tint === 'amber' ? 'bg-amber-400 border-black' : 'bg-black border-white'}`}/>
      <span className="font-semibold tabular-nums">{label}</span>
    </div>
  );
}

function MarkerTip({ tip }) {
  return (
    <div className="pbb-gmap-tip" style={{ left: `${tip.x}%`, top: `${tip.y}%` }} data-testid={`map-tip-${tip.id}`}>
      <div className="flex items-center gap-1 mb-1">
        {tip.paid
          ? <DollarSign size={11} className="text-amber-400"/>
          : <Zap size={11} className="text-white"/>}
        <span className={`text-[9px] uppercase tracking-widest font-black ${tip.paid ? 'text-amber-300' : 'text-white'}`}>
          {tip.paid ? 'Active · Paid' : 'Discovered'}
        </span>
      </div>
      <div className="font-bold text-[13px] leading-tight">{tip.name}</div>
      {tip.category && <div className="text-[11px] text-neutral-400 mt-0.5">{tip.category}</div>}
      {(tip.city || tip.state) && (
        <div className="text-[11px] text-neutral-400 mt-0.5">
          <MapPin size={10} className="inline mr-0.5"/>{[tip.city, tip.state].filter(Boolean).join(', ')}
        </div>
      )}
      <div className="text-[10px] text-neutral-500 mt-1 italic">{tip.status}</div>
    </div>
  );
}

function MarkerDetailModal({ kind, id, seed, onClose }) {
  const [data, setData] = useState(seed || null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const path = (kind === 'paid' || seed?.isBusiness) ? `/map/business/${id}` : `/map/discovery/${id}`;
    http.get(path)
      .then(r => setData({ ...(seed || {}), ...r.data }))
      .catch(() => setData(seed || null))
      .finally(() => setLoading(false));
  }, [kind, id, seed]);

function ClaimCta({ data }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const onClick = async () => {
    if (!user) {
      // Send guest through signup, then back to the Choose Plan page with
      // the business id so claim + subscription can complete in one flow.
      navigate(`/signup?claim=${data.id}&next=/plans?claim=${data.id}`);
      return;
    }
    setBusy(true);
    try {
      await blackout.claimBusiness(data.id, { verifyName: user.name, verifyEmail: user.email });
      setDone(true);
      toast({
        title: 'Listing claimed',
        description: 'Finish by choosing a plan — your discoverer earns +100 points on activation.',
      });
      setTimeout(() => navigate(`/plans?claim=${data.id}`), 900);
    } catch (e) {
      toast({ title: 'Claim failed', description: e?.response?.data?.detail || 'Try again' });
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500/20 border border-emerald-500/50 text-emerald-200 text-sm font-bold" data-testid="map-detail-claimed">
        <Check size={14}/> Claimed — redirecting to Plans…
      </div>
    );
  }
  return (
    <button onClick={onClick} disabled={busy} data-testid="map-detail-claim-cta"
      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-bold disabled:opacity-60">
      {busy ? <Loader2 size={14} className="animate-spin"/> : <UserPlus size={14}/>}
      Claim this listing
    </button>
  );
}


  const close = () => onClose?.();
  const paid = kind === 'paid';
  const location = [data?.city, data?.state].filter(Boolean).join(', ');

  return (
    <div className="fixed inset-0 z-[90] bg-black/80 flex items-center justify-center p-4" onClick={close} data-testid="map-detail-modal">
      <div className="w-full max-w-lg bg-[#0a0a0a] border border-amber-500/40 rounded-2xl overflow-hidden shadow-2xl shadow-amber-500/20" onClick={e => e.stopPropagation()}>
        {/* Header strip */}
        <div className={`relative h-24 overflow-hidden ${paid
          ? 'bg-gradient-to-br from-amber-500 via-amber-600 to-amber-900'
          : 'bg-gradient-to-br from-neutral-800 via-neutral-900 to-black'}`}>
          {data?.coverUrl && (
            <img src={data.coverUrl} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60"/>
          )}
          <button onClick={close} data-testid="map-detail-close"
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/60 border border-white/20 text-white/90 flex items-center justify-center hover:bg-black">
            <X size={16}/>
          </button>
          <div className="absolute left-4 bottom-3 flex items-center gap-2">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest ${paid ? 'bg-black/80 text-amber-300 border border-amber-400' : 'bg-black/80 text-white border border-white/40'}`}>
              {paid ? <><DollarSign size={10}/> Active · Paid</> : <><Zap size={10}/> Discovered</>}
            </span>
          </div>
        </div>

        <div className="p-5">
          {loading && !data?.name ? (
            <div className="py-6 text-center text-neutral-400 text-sm inline-flex items-center gap-2 justify-center w-full">
              <Loader2 className="animate-spin" size={14}/> Loading details…
            </div>
          ) : (
            <>
              <div className="flex items-start gap-3">
                {data?.logoUrl && (
                  <img src={data.logoUrl} alt="" className="w-14 h-14 rounded-xl object-cover border border-neutral-800"/>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="text-xl font-black text-white truncate" data-testid="map-detail-name">{data?.name || 'Unnamed'}</h3>
                  {data?.category && <div className="text-xs text-amber-300 font-semibold mt-0.5">{data.category}</div>}
                  {location && (
                    <div className="text-xs text-neutral-400 mt-1 inline-flex items-center gap-1">
                      <MapPin size={11}/> {location}
                    </div>
                  )}
                </div>
              </div>

              {data?.description && (
                <p className="mt-4 text-sm text-neutral-300 leading-relaxed line-clamp-5">{data.description}</p>
              )}

              {paid && data?.address && (
                <div className="mt-3 text-xs text-neutral-500">{data.address}</div>
              )}

              <div className="mt-5 flex flex-wrap gap-2">
                {paid ? (
                  <>
                    {data?.claimable && <ClaimCta data={data}/>}
                    <Link to={`/directory/${data.id}`} data-testid="map-detail-directory-link"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black text-sm font-bold">
                      View in Directory <ArrowRight size={14}/>
                    </Link>
                    {data?.website && (
                      <a href={data.website} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-neutral-700 hover:border-amber-500/60 text-sm text-neutral-200">
                        Visit website <ExternalLink size={13}/>
                      </a>
                    )}
                  </>
                ) : (
                  <>
                    <Link to="/signup" data-testid="map-detail-signup-link"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-bold">
                      Register to track this <ArrowRight size={14}/>
                    </Link>
                    {data?.website && (
                      <a href={data.website} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-neutral-700 hover:border-emerald-500/60 text-sm text-neutral-200">
                        Source link <ExternalLink size={13}/>
                      </a>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

